import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import { TrashIcon, PencilIcon, ChatBubbleLeftIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/outline';

import TableBasedKanban from './TableBasedKanban';
import ProjectCalendar from './ProjectCalendar';
import ProjectGridWithRaci from './ProjectGridWithRaci';
import AddPhaseButton from '../phases/AddPhaseButton';
import ProjectNotes from './ProjectNotes';
import ProjectFiles from './ProjectFiles';
import ProjectMissingItems from './ProjectMissingItems';
import AddStepButton from '../steps/AddStepButton';
import RaciAssignments from '../assignments/RaciAssignments';
import ProjectRaciAssignments from '../assignments/ProjectRaciAssignments';

function ProjectDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'raci');
  const [showContactSelector, setShowContactSelector] = useState(false);
  const [showDueDateReasonModal, setShowDueDateReasonModal] = useState(false);
  const [dueDateChangeReason, setDueDateChangeReason] = useState('');
  
  // Edit form state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('Active');
  const [editDepartmentId, setEditDepartmentId] = useState('');
  const [editGroupId, setEditGroupId] = useState('');
  const [editTypeId, setEditTypeId] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editFolderLink, setEditFolderLink] = useState('');
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [types, setTypes] = useState([]);
  const [phaseStatusTypes, setPhaseStatusTypes] = useState([]);
  const [stepStatusTypes, setStepStatusTypes] = useState([]);
  
  // Project Metadata section with inline editing
  const [inlineEditing, setInlineEditing] = useState({
    status: false,
    department: false,
    dueDate: false,
    startDate: false,
    type: false,
    group: false,
    folderLink: false
  });
  
  const [inlineEditValues, setInlineEditValues] = useState({
    status: '',
    department: '',
    dueDate: '',
    startDate: '',
    type: '',
    group: '',
    folderLink: ''
  });
  
  // Function to format date for the date input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      // Handle string dates in various formats
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return '';
      }
      
      // Adjust for timezone issues - use UTC methods to avoid timezone shift
      // This ensures we get the date as it's stored, not shifted by local timezone
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-indexed
      const day = String(date.getUTCDate()).padStart(2, '0');
      
      const formatted = `${year}-${month}-${day}`;
      return formatted;
    } catch (e) {
      console.warn('Error formatting date for input:', dateString, e);
      return '';
    }
  };
  
  // Function to format a date for display in a consistent way
  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'Not set';
    
    // Use our UTC-aware function to get YYYY-MM-DD format
    const formattedDate = formatDateForInput(dateString);
    
    // Convert YYYY-MM-DD to MM/DD/YYYY
    if (formattedDate) {
      const [year, month, day] = formattedDate.split('-');
      return `${month}/${day}/${year}`;
    }
    
    return 'Not set';
  };
  
  // Define fetchAllData outside useEffect so it can be referenced elsewhere
  const fetchAllData = async (options = {}) => {
    // Only show loading if not silent mode
    if (!options.silent) {
      setLoading(true);
    }
    setError(null);
    
    try {
      console.log('Starting to fetch all metadata...');
      
      // 1. Project statuses
      const projectStatusResponse = await api.get('/settings/status-types/project');
      if (projectStatusResponse.data?.statusTypes) {
        setAvailableStatuses(projectStatusResponse.data.statusTypes);
      }
      
      // 2. Phase statuses
      const phaseResponse = await api.get('/settings/status-types/phase');
      if (phaseResponse.data?.statusTypes) {
        setPhaseStatusTypes(phaseResponse.data.statusTypes);
      }
      
      // 3. Step statuses
      const stepResponse = await api.get('/settings/status-types/step');
      if (stepResponse.data?.statusTypes) {
        setStepStatusTypes(stepResponse.data.statusTypes);
      }
      
      // 4. Departments
      const departmentsResponse = await api.get('/departments');
      const departmentsData = departmentsResponse.data?.departments || [];
      setDepartments(departmentsData);
      
      // 5. Groups
      const groupsResponse = await api.get('/groups');
      const groupsData = groupsResponse.data?.groups || [];
      setGroups(groupsData);
      
      // 6. Types
      const typesResponse = await api.get('/types');
      const typesData = typesResponse.data?.types || [];
      setTypes(typesData);
      
      console.log('All metadata loaded successfully');
      
      // 7. Now fetch project with all metadata available
      const projectResponse = await api.get(`/projects/${id}`);
      
      if (projectResponse.data?.project) {
        const projectData = projectResponse.data.project;
        
        // Ensure phases is an array
        if (!projectData.phases) {
          projectData.phases = [];
        } else if (typeof projectData.phases === 'string') {
          try {
            projectData.phases = JSON.parse(projectData.phases);
          } catch (e) {
            console.error('Error parsing phases JSON:', e);
            projectData.phases = [];
          }
        }
        
        console.log('Project data:', projectData);
        
        // Set project and form state
        setProject(projectData);
        setEditName(projectData.name || '');
        setEditDescription(projectData.description || '');
        setEditStatus(projectData.status || 'Not Started');
        setEditDepartmentId(projectData.department_id?.toString() || '');
        setEditGroupId(projectData.group_id?.toString() || '');
        setEditTypeId(projectData.type_id?.toString() || '');
        setEditStartDate(formatDateForInput(projectData.start_date) || '');
        setEditDueDate(formatDateForInput(projectData.due_date) || '');
        setEditFolderLink(projectData.folder_link || '');
      } else {
        throw new Error('Invalid project data structure');
      }
    } catch (err) {
      console.error('Error in data fetching:', err);
      setError('Failed to load project data. Please try again later.');
    } finally {
      // Only hide loading if we showed it or if there was an error
      if (!options.silent || error) {
        setLoading(false);
      }
    }
  };
  
  // Use fetchAllData in useEffect
  useEffect(() => {
    fetchAllData();
  }, [id]);
  
  // Alias for backward compatibility with other components
  const fetchProjectDetails = fetchAllData;
  
  // Expose fetchProjectDetails on window for other components to use
  useEffect(() => {
    window.fetchProjectDetails = fetchProjectDetails;
    
    return () => {
      // Clean up when component unmounts
      delete window.fetchProjectDetails;
    };
  }, []);
  
  // Initialize inline edit values when project data loads
  useEffect(() => {
    if (project) {
      setInlineEditValues({
        status: project.status || '',
        department: project.department_id?.toString() || '',
        dueDate: formatDateForInput(project.due_date) || '',
        startDate: formatDateForInput(project.start_date) || '',
        type: project.type_id?.toString() || '',
        group: project.group_id?.toString() || '',
        folderLink: project.folder_link || ''
      });
      
      // Update edit form values whenever project changes
      setEditName(project.name || '');
      setEditDescription(project.description || '');
      setEditStatus(project.status || 'Not Started');
      setEditDepartmentId(project.department_id?.toString() || '');
      setEditGroupId(project.group_id?.toString() || '');
      setEditTypeId(project.type_id?.toString() || '');
      setEditStartDate(formatDateForInput(project.start_date) || '');
      setEditDueDate(formatDateForInput(project.due_date) || '');
      setEditFolderLink(project.folder_link || '');
    }
  }, [project]);
  
  // Handle toggling the inline edit mode for a field
  const toggleInlineEdit = (field) => {
    setInlineEditing({
      ...inlineEditing,
      [field]: !inlineEditing[field]
    });
    
    // Reset field value to current project value if cancelling
    if (inlineEditing[field]) {
      setInlineEditValues({
        ...inlineEditValues,
        status: project.status || '',
        department: project.department_id?.toString() || '',
        dueDate: formatDateForInput(project.due_date) || '',
        startDate: formatDateForInput(project.start_date) || '',
        type: project.type_id?.toString() || '',
        group: project.group_id?.toString() || '',
        folderLink: project.folder_link || ''
      });
    }
  };
  
  // Handle saving inline edits for a field
  const saveInlineEdit = async (field) => {
    try {
      const updateData = {};
      
      switch (field) {
        case 'status':
          updateData.status = inlineEditValues.status;
          break;
        case 'department':
          updateData.department_id = inlineEditValues.department ? parseInt(inlineEditValues.department, 10) : null;
          break;
        case 'dueDate':
          // Special handling for due date changes
          if (project.due_date !== inlineEditValues.dueDate) {
            // If due date is changing, don't save yet - open reason modal
            setShowDueDateReasonModal(true);
            return;
          }
          updateData.due_date = inlineEditValues.dueDate || null;
          break;
        case 'startDate':
          updateData.start_date = inlineEditValues.startDate || null;
          break;
        case 'type':
          updateData.type_id = inlineEditValues.type ? parseInt(inlineEditValues.type, 10) : null;
          break;
        case 'group':
          updateData.group_id = inlineEditValues.group ? parseInt(inlineEditValues.group, 10) : null;
          break;
        case 'folderLink':
          updateData.folder_link = inlineEditValues.folderLink || null;
          break;
        default:
          break;
      }
      
      // Make API call to update the project
      await api.put(`/projects/${id}`, updateData);
      
      // Update was successful, refresh project data
      await fetchAllData();
      
      // Exit edit mode
      setInlineEditing({
        ...inlineEditing,
        [field]: false
      });
      
      toast.success(`Project ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} updated`);
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      toast.error(`Failed to update ${field}`);
    }
  };
  
  // Save due date with reason
  const saveDueDateWithReason = async () => {
    if (!dueDateChangeReason.trim()) {
      toast.error('Please provide a reason for changing the due date');
      return;
    }
    
    try {
      // Make API call to update the project with due date and reason
      await api.put(`/projects/${id}`, {
        due_date: inlineEditValues.dueDate || null,
        due_date_change_reason: dueDateChangeReason
      });
      
      // Update was successful, refresh project data
      await fetchAllData();
      
      // Exit edit mode and close modal
      setInlineEditing({
        ...inlineEditing,
        dueDate: false
      });
      
      setShowDueDateReasonModal(false);
      setDueDateChangeReason('');
      
      toast.success('Project due date updated');
    } catch (error) {
      console.error('Error updating due date:', error);
      toast.error('Failed to update due date');
    }
  };
  
  // Cancel due date change
  const cancelDueDateChange = () => {
    // Reset to current project value
    setInlineEditValues({
      ...inlineEditValues,
      dueDate: formatDateForInput(project.due_date) || ''
    });
    
    // Exit edit mode and close modal
    setInlineEditing({
      ...inlineEditing,
      dueDate: false
    });
    
    setShowDueDateReasonModal(false);
    setDueDateChangeReason('');
  };
  
  // Handle change in inline edit values
  const handleInlineEditChange = (field, value) => {
    setInlineEditValues({
      ...inlineEditValues,
      [field]: value
    });
  };
  
  const handleStepClick = (step) => {
    // If the step doesn't have a proper database ID, show a message
    if (!step.id) {
      alert('This is a template step without a database ID. To interact with steps, they need to be properly created in the database.');
      return;
    }
    
    // Navigate to the step details page
    navigate(`/steps/${step.id}`);
  };
  
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await api.delete(`/projects/${id}`);
      
      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Project deleted successfully');
        navigate('/projects');
      } else {
        throw new Error('Failed to delete project');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error(error.response?.data?.error || 'Failed to delete project. Please try again.');
      setIsDeleting(false);
    }
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsEditing(true);
    
    // Convert empty string IDs to null for integer fields
    const department_id = editDepartmentId ? parseInt(editDepartmentId, 10) : null;
    const group_id = editGroupId ? parseInt(editGroupId, 10) : null;
    const type_id = editTypeId ? parseInt(editTypeId, 10) : null;
    
    // Check if due date is changing using formatDateForInput for proper comparison
    const formattedOriginalDate = formatDateForInput(project.due_date);
    const isDueDateChanging = editDueDate !== formattedOriginalDate;
    
    console.log('Due date comparison:', {
      'Original due date': project.due_date,
      'Formatted original date': formattedOriginalDate,
      'Edit form due date': editDueDate,
      'Is changing': isDueDateChanging
    });
    
    // If due date is changing and no reason is provided, show the modal
    if (isDueDateChanging && !dueDateChangeReason) {
      // Set up the values for when the modal closes
      setInlineEditValues({
        ...inlineEditValues,
        dueDate: editDueDate || ''
      });
      setShowDueDateReasonModal(true);
      setIsEditing(false);
      return;
    }
    
    const projectData = {
      name: editName,
      description: editDescription,
      status: editStatus,
      department_id,
      group_id,
      type_id,
      start_date: editStartDate || null,
      due_date: editDueDate || null,
      folder_link: editFolderLink || null
    };
    
    // If due date is changing, add the reason
    if (isDueDateChanging) {
      projectData.due_date_change_reason = dueDateChangeReason;
    }
    
    console.log('Submitting project update:', projectData);
    
    try {
      const response = await api.put(`/projects/${id}`, projectData);
      
      console.log('Edit response received:', response.data);
      
      // Close the modal first for better UX
      setShowEditModal(false);
      
      // Reset the due date change reason
      setDueDateChangeReason('');
      
      // Always fetch fresh project details to ensure we have all metadata
      await fetchAllData();
      
      toast.success('Project updated successfully');
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Failed to update project. Please try again.');
    } finally {
      setIsEditing(false);
    }
  };
  
  const handlePhaseAdded = (newPhase) => {
    // Refresh project data to include the new phase
    fetchAllData();
  };
  
  const handleStepAdded = (phaseId) => {
    // Refresh project data to include the new step
    fetchAllData();
  };
  
  // Add useEffect to inspect DOM elements after render
  useEffect(() => {
    if (project && project.phases && project.phases.length > 0 && activeTab === 'timeline') {
      // Give React time to render the DOM
      setTimeout(() => {
        console.log("DOM Inspection - Looking for Kanban elements");
        
        const kanbanContainer = document.getElementById('kanban-container-debug');
        if (kanbanContainer) {
          console.log("DOM Inspection - Found kanban container:", kanbanContainer);
          const computedStyle = window.getComputedStyle(kanbanContainer);
          console.log("DOM Inspection - Container computed style:", {
            display: computedStyle.display,
            overflowX: computedStyle.overflowX,
            padding: computedStyle.padding,
            backgroundColor: computedStyle.backgroundColor,
            width: computedStyle.width
          });
        } else {
          console.error("DOM Inspection - Kanban container not found");
        }
        
        const kanbanBoard = document.getElementById('kanban-board-debug');
        if (kanbanBoard) {
          console.log("DOM Inspection - Found kanban board:", kanbanBoard);
          const computedStyle = window.getComputedStyle(kanbanBoard);
          console.log("DOM Inspection - Board computed style:", {
            display: computedStyle.display,
            flexDirection: computedStyle.flexDirection,
            flexWrap: computedStyle.flexWrap
          });
          
          // Check if the board has any child phase columns
          console.log("DOM Inspection - Board children count:", kanbanBoard.children.length);
        } else {
          console.error("DOM Inspection - Kanban board not found");
        }
        
        // Check first phase column if available
        if (project.phases[0] && project.phases[0].id) {
          const firstPhaseColumn = document.getElementById(`phase-column-${project.phases[0].id}-debug`);
          if (firstPhaseColumn) {
            console.log("DOM Inspection - Found first phase column:", firstPhaseColumn);
            const computedStyle = window.getComputedStyle(firstPhaseColumn);
            console.log("DOM Inspection - First phase column computed style:", {
              display: computedStyle.display,
              flexDirection: computedStyle.flexDirection,
              width: computedStyle.width,
              minWidth: computedStyle.minWidth,
              position: computedStyle.position
            });
          } else {
            console.error("DOM Inspection - First phase column not found");
          }
        }
      }, 500); // 500ms delay to ensure DOM is updated
    }
  }, [project, activeTab]);
  
  // Update URL when activeTab changes
  useEffect(() => {
    // Update the URL query parameter when the tab changes
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);
  
  // Modified tab click handlers to work with both state and URL
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-12 h-12 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading project details...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-2 text-red-700 underline"
        >
          Retry
        </button>
      </div>
    );
  }
  
  if (!project) {
    return (
      <div className="p-4 text-yellow-700 bg-yellow-100 rounded-md">
        <p>Project not found.</p>
        <Link to="/projects" className="mt-2 text-yellow-700 underline">
          View all projects
        </Link>
      </div>
    );
  }
  
  return (
    <div>
      {/* Header with Project Title and Action Buttons */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex flex-col md:flex-row justify-between items-start md:items-center">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
            {project.name}
          </h2>
          <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
            <button
              type="button"
              onClick={() => setShowEditModal(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PencilIcon className="h-5 w-5 mr-2" aria-hidden="true" />
              Edit Project
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <TrashIcon className="h-5 w-5 mr-2" aria-hidden="true" />
              Delete Project
            </button>
          </div>
        </div>
        
        {/* Project Metadata */}
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              {/* Left Column */}
              <div className="flex items-center">
                <span className="w-28 text-sm font-medium text-gray-600">Client:</span>
                <span className="text-sm text-gray-900">{project.client ? project.client.name : 'Not assigned'}</span>
              </div>
              
              <div className="flex items-center">
                <span className="w-28 text-sm font-medium text-gray-600">Status:</span>
                {inlineEditing.status ? (
                  <div className="flex items-center">
                    <select
                      value={inlineEditValues.status}
                      onChange={(e) => handleInlineEditChange('status', e.target.value)}
                      className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1"
                    >
                      {availableStatuses.map((status) => (
                        <option key={status.id} value={status.name}>{status.name}</option>
                      ))}
                    </select>
                    <button 
                      onClick={() => saveInlineEdit('status')}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => toggleInlineEdit('status')}
                      className="ml-1 text-red-600 hover:text-red-800"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: availableStatuses.find(s => s.name === project.status)?.color || '#6B7280',
                        color: 'white'
                      }}
                    >
                      {project.status}
                    </span>
                    <button 
                      onClick={() => toggleInlineEdit('status')}
                      className="ml-2 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                      title="Edit status"
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex items-center">
                <span className="w-28 text-sm font-medium text-gray-600">Department:</span>
                {inlineEditing.department ? (
                  <div className="flex items-center">
                    <select
                      value={inlineEditValues.department}
                      onChange={(e) => handleInlineEditChange('department', e.target.value)}
                      className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1"
                    >
                      <option value="">Select department</option>
                      {departments.map((department) => (
                        <option key={department.id} value={department.id}>{department.name}</option>
                      ))}
                    </select>
                    <button 
                      onClick={() => saveInlineEdit('department')}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => toggleInlineEdit('department')}
                      className="ml-1 text-red-600 hover:text-red-800"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: departments.find(d => parseInt(d.id, 10) === parseInt(project.department_id, 10))?.color || '#6B7280',
                        color: 'white'
                      }}
                    >
                      {departments.find(d => parseInt(d.id, 10) === parseInt(project.department_id, 10))?.name || 'Default'}
                    </span>
                    <button 
                      onClick={() => toggleInlineEdit('department')}
                      className="ml-2 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                      title="Edit department"
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              {/* Right Column */}
              <div className="flex items-center">
                <span className="w-28 text-sm font-medium text-gray-600">Due Date:</span>
                {inlineEditing.dueDate ? (
                  <div className="flex items-center">
                    <input
                      type="date"
                      value={inlineEditValues.dueDate}
                      onChange={(e) => handleInlineEditChange('dueDate', e.target.value)}
                      className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1"
                    />
                    <button 
                      onClick={() => saveInlineEdit('dueDate')}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => toggleInlineEdit('dueDate')}
                      className="ml-1 text-red-600 hover:text-red-800"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span className="text-sm text-gray-900">
                      {formatDisplayDate(project.due_date)}
                    </span>
                    <button 
                      onClick={() => toggleInlineEdit('dueDate')}
                      className="ml-2 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                      title="Edit due date"
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex items-center">
                <span className="w-28 text-sm font-medium text-gray-600">Start Date:</span>
                {inlineEditing.startDate ? (
                  <div className="flex items-center">
                    <input
                      type="date"
                      value={inlineEditValues.startDate}
                      onChange={(e) => handleInlineEditChange('startDate', e.target.value)}
                      className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1"
                    />
                    <button 
                      onClick={() => saveInlineEdit('startDate')}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => toggleInlineEdit('startDate')}
                      className="ml-1 text-red-600 hover:text-red-800"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span className="text-sm text-gray-900">
                      {formatDisplayDate(project.start_date)}
                    </span>
                    <button 
                      onClick={() => toggleInlineEdit('startDate')}
                      className="ml-2 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                      title="Edit start date"
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex items-center">
                <span className="w-28 text-sm font-medium text-gray-600">Type:</span>
                {inlineEditing.type ? (
                  <div className="flex items-center">
                    <select
                      value={inlineEditValues.type}
                      onChange={(e) => handleInlineEditChange('type', e.target.value)}
                      className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1"
                    >
                      <option value="">Select type</option>
                      {types.map((type) => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                    <button 
                      onClick={() => saveInlineEdit('type')}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => toggleInlineEdit('type')}
                      className="ml-1 text-red-600 hover:text-red-800"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: types.find(t => parseInt(t.id, 10) === parseInt(project.type_id, 10))?.color || '#6B7280',
                        color: 'white'
                      }}
                    >
                      {types.find(t => parseInt(t.id, 10) === parseInt(project.type_id, 10))?.name || 'Default'}
                    </span>
                    <button 
                      onClick={() => toggleInlineEdit('type')}
                      className="ml-2 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                      title="Edit type"
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex items-center">
                <span className="w-28 text-sm font-medium text-gray-600">Group:</span>
                {inlineEditing.group ? (
                  <div className="flex items-center">
                    <select
                      value={inlineEditValues.group}
                      onChange={(e) => handleInlineEditChange('group', e.target.value)}
                      className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1"
                    >
                      <option value="">Select group</option>
                      {groups.map((group) => (
                        <option key={group.id} value={group.id}>{group.name}</option>
                      ))}
                    </select>
                    <button 
                      onClick={() => saveInlineEdit('group')}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => toggleInlineEdit('group')}
                      className="ml-1 text-red-600 hover:text-red-800"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: groups.find(g => parseInt(g.id, 10) === parseInt(project.group_id, 10))?.color || '#6B7280',
                        color: 'white'
                      }}
                    >
                      {groups.find(g => parseInt(g.id, 10) === parseInt(project.group_id, 10))?.name || 'Default'}
                    </span>
                    <button 
                      onClick={() => toggleInlineEdit('group')}
                      className="ml-2 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                      title="Edit group"
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex items-center">
                <span className="w-28 text-sm font-medium text-gray-600">Folder Link:</span>
                {inlineEditing.folderLink ? (
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={inlineEditValues.folderLink}
                      onChange={(e) => handleInlineEditChange('folderLink', e.target.value)}
                      className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1"
                      placeholder="Enter folder link"
                    />
                    <button 
                      onClick={() => saveInlineEdit('folderLink')}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => toggleInlineEdit('folderLink')}
                      className="ml-1 text-red-600 hover:text-red-800"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span className="text-sm text-gray-900">
                      {project.folder_link ? (
                        <a 
                          href={project.folder_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                          title={project.folder_link}
                        >
                          {project.folder_link.length > 30 
                            ? `${project.folder_link.substring(0, 30)}...` 
                            : project.folder_link}
                        </a>
                      ) : (
                        'Not set'
                      )}
                    </span>
                    <button 
                      onClick={() => toggleInlineEdit('folderLink')}
                      className="ml-2 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                      title="Edit folder link"
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Additional Metadata Row (Optional) */}
          <div className="flex flex-wrap mt-4 border-t border-gray-100 pt-3">
            <div className="mr-6 mt-2">
              <span className="text-xs font-medium text-gray-500">CREATED:</span>
              <span className="ml-2 text-sm text-gray-700">{formatDisplayDate(project.created_at)}</span>
            </div>
            
            {project.budget && (
              <div className="mt-2">
                <span className="text-xs font-medium text-gray-500">BUDGET:</span>
                <span className="ml-2 text-sm text-gray-700">{project.budget}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Project Description */}
      {project.description && (
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg font-medium text-gray-900">
              Description
            </h3>
            <div className="mt-2 text-sm text-gray-500">
              <p>{project.description}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* RACI Grid */}
      <ProjectRaciAssignments projectId={project.id} />
      
      {/* Tabs Navigation */}
      <div className="bg-white shadow rounded-lg mb-6 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-4" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('raci')}
              className={`${
                activeTab === 'raci'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              RACI
            </button>
            <button
              onClick={() => handleTabChange('timeline')}
              className={`${
                activeTab === 'timeline'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Timeline
            </button>
            <button
              onClick={() => handleTabChange('notes')}
              className={`${
                activeTab === 'notes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Notes
            </button>
            <button
              onClick={() => handleTabChange('files')}
              className={`${
                activeTab === 'files'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Files
            </button>
            <button
              onClick={() => handleTabChange('missing')}
              className={`${
                activeTab === 'missing'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Missing Items
            </button>
          </nav>
        </div>
        
        {/* Tab Content */}
        <div className="px-4 py-5 sm:px-6">
          {activeTab === 'raci' ? (
            <div>
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-medium text-white">RACI Assignment View</h3>
                <AddPhaseButton projectId={project.id} onPhaseAdded={handlePhaseAdded} />
              </div>
              <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                <ProjectGridWithRaci 
                  phases={project.phases}
                  phaseStatusTypes={phaseStatusTypes}
                  stepStatusTypes={stepStatusTypes}
                  handleStepClick={handleStepClick}
                  handleStepAdded={handleStepAdded}
                  handlePhaseClick={(phaseId) => navigate(`/phases/${phaseId}`)}
                  projectId={project.id}
                />
              </div>
            </div>
          ) : activeTab === 'timeline' ? (
            <div>
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-medium text-gray-900">Project Calendar</h3>
                <AddPhaseButton projectId={project.id} onPhaseAdded={handlePhaseAdded} />
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <ProjectCalendar 
                  project={project}
                  phaseStatusTypes={phaseStatusTypes}
                  stepStatusTypes={stepStatusTypes}
                  handleStepClick={handleStepClick}
                  handlePhaseClick={(phaseId) => navigate(`/phases/${phaseId}`)}
                />
              </div>
            </div>
          ) : activeTab === 'notes' ? (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-5">Project Notes</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <ProjectNotes projectId={project.id} />
              </div>
            </div>
          ) : activeTab === 'files' ? (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-5">Project Files</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <ProjectFiles projectId={project.id} />
              </div>
            </div>
          ) : activeTab === 'missing' ? (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-5">Missing Information</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <ProjectMissingItems projectId={project.id} />
              </div>
            </div>
          ) : null}
        </div>
      </div>
      
      {/* Edit Project Modal */}
      {showEditModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleEditSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <PencilIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Edit Project
                      </h3>
                      <div className="mt-4 space-y-4">
                        {/* Project name field */}
                        <div>
                          <label htmlFor="editName" className="block text-sm font-medium text-gray-700">
                            Project Name
                          </label>
                          <input
                            type="text"
                            name="editName"
                            id="editName"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            required
                          />
                        </div>
                        
                        {/* Project description field */}
                        <div>
                          <label htmlFor="editDescription" className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            name="editDescription"
                            id="editDescription"
                            rows="3"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          ></textarea>
                        </div>
                        
                        {/* Project status field */}
                        <div>
                          <label htmlFor="editStatus" className="block text-sm font-medium text-gray-700">
                            Status
                          </label>
                          <select
                            name="editStatus"
                            id="editStatus"
                            value={editStatus}
                            onChange={(e) => setEditStatus(e.target.value)}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                          >
                            {availableStatuses.map((status) => (
                              <option key={status.name} value={status.name}>{status.name}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Group these fields - department, type, group */}
                        <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                          <div>
                            <label htmlFor="editDepartmentId" className="block text-sm font-medium text-gray-700">
                              Department
                            </label>
                            <select
                              name="editDepartmentId"
                              id="editDepartmentId"
                              value={editDepartmentId}
                              onChange={(e) => setEditDepartmentId(e.target.value)}
                              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                            >
                              <option value="">Select department...</option>
                              {departments.map((department) => (
                                <option key={department.id} value={department.id.toString()}>
                                  {department.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label htmlFor="editTypeId" className="block text-sm font-medium text-gray-700">
                              Type
                            </label>
                            <select
                              name="editTypeId"
                              id="editTypeId"
                              value={editTypeId}
                              onChange={(e) => setEditTypeId(e.target.value)}
                              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                            >
                              <option value="">Select type...</option>
                              {types.map((type) => (
                                <option key={type.id} value={type.id.toString()}>
                                  {type.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label htmlFor="editGroupId" className="block text-sm font-medium text-gray-700">
                              Group
                            </label>
                            <select
                              name="editGroupId"
                              id="editGroupId"
                              value={editGroupId}
                              onChange={(e) => setEditGroupId(e.target.value)}
                              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                            >
                              <option value="">Select group...</option>
                              {groups.map((group) => (
                                <option key={group.id} value={group.id.toString()}>
                                  {group.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        {/* Date fields */}
                        <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                          <div>
                            <label htmlFor="editStartDate" className="block text-sm font-medium text-gray-700">
                              Start Date
                            </label>
                            <input
                              type="date"
                              name="editStartDate"
                              id="editStartDate"
                              value={editStartDate}
                              onChange={(e) => setEditStartDate(e.target.value)}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                          
                          <div>
                            <label htmlFor="editDueDate" className="block text-sm font-medium text-gray-700">
                              Due Date
                            </label>
                            <input
                              type="date"
                              name="editDueDate"
                              id="editDueDate"
                              value={editDueDate}
                              onChange={(e) => setEditDueDate(e.target.value)}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                        </div>
                        
                        {/* Due Date Change Reason field (only shown if due date is changing) */}
                        {project && editDueDate !== formatDateForInput(project.due_date) && (
                          <div>
                            <label htmlFor="dueDateChangeReason" className="block text-sm font-medium text-gray-700">
                              Due Date Change Reason <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              name="dueDateChangeReason"
                              id="dueDateChangeReason"
                              rows="3"
                              value={dueDateChangeReason}
                              onChange={(e) => setDueDateChangeReason(e.target.value)}
                              className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              placeholder="Required reason for changing due date"
                              required={project && editDueDate !== formatDateForInput(project.due_date)}
                            ></textarea>
                          </div>
                        )}
                        
                        {/* Folder Link field */}
                        <div>
                          <label htmlFor="editFolderLink" className="block text-sm font-medium text-gray-700">
                            Folder Link
                          </label>
                          <input
                            type="text"
                            name="editFolderLink"
                            id="editFolderLink"
                            value={editFolderLink}
                            onChange={(e) => setEditFolderLink(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="e.g. https://drive.google.com/drive/folders/..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isEditing}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {isEditing ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Project
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this project? All phases, steps, tasks, and resources associated with this project will be permanently removed. This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleDelete}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${isDeleting ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Due Date Change Reason Modal */}
      {showDueDateReasonModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="due-date-reason-modal" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="due-date-reason-modal-title">
                      Due Date Change Reason
                    </h3>
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-4">
                        Please provide a reason for changing the project due date.
                      </p>
                      <textarea
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        rows="3"
                        placeholder="Enter reason for due date change..."
                        value={dueDateChangeReason}
                        onChange={(e) => setDueDateChangeReason(e.target.value)}
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={saveDueDateWithReason}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={cancelDueDateChange}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectDetails; 